import React, { useEffect, useRef, useState } from 'react';
import { useFragment } from 'react-relay/hooks';

import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { find } from 'lodash';

import useSocketEventsDebounced from 'hooks/useSocketEventsDebounced';

import { ChatMessageTyping_EmbedNode$key } from './__generated__/ChatMessageTyping_EmbedNode.graphql';
import AvatarNode from './AvatarNode';

// TODO cleanup ChatMessageTyping styles
// NOTE wait until design decides if we move this to the chat header
const styles = ({
  custom: {
    // @ts-ignore
    palette: { colorDarkGray, colorGrayishBlue, colorAccentSunrise, colorAccentPeach, colorMessageBubble },
  },
  palette: {
    common: { white },
    primary,
    text,
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    broadcast: {},
    chatAudit: {},
    typing: {},
    typingIndicator: {
      backgroundColor: primary.main,
      borderRadius: 5,
      display: 'table',
      marginTop: unit / 4,
      padding: unit,
      position: 'relative',
      width: 'auto',
      willChange: 'transform',
      '& span': {
        height: 4,
        width: 4,
        float: 'left',
        margin: '0 1px',
        backgroundColor: white,
        display: 'block',
        borderRadius: '50%',
        opacity: 0.8,
        '&:nth-of-type(1)': {
          animation: '1s blink infinite 0.3s',
        },
        '&:nth-of-type(2)': {
          animation: '1s blink infinite 0.6s',
        },
        '&:nth-of-type(3)': {
          animation: '1s blink infinite 0.9s',
        },
      },
    },
    internal: {},
    messageWrapper: {
      alignItems: 'flex-start',
      backgroundColor: colorGrayishBlue,
      position: 'relative',
      border: '1px',
      borderRadius: '15px 15px 15px 0px',
      color: text.primary,
      display: 'flex',
      flexDirection: 'column',
      fontWeight: 500,
      marginLeft: unit / 2,
      marginRight: 0,
      maxWidth: '80%',
      padding: '10px 15px',
      '&$outbound': {
        backgroundColor: colorMessageBubble,
        borderRadius: '15px 15px 0px 15px !important',
        color: `${white} !important`,
        alignItems: 'flex-end !important',
        marginLeft: '0px !important',
        marginRight: `${unit / 2}px !important`,
      },
      '&$internal': {
        backgroundColor: `${colorAccentSunrise} !important`,
        border: `1px solid ${colorAccentPeach} !important`,
        color: `${text.primary} !important`,
      },
      '&$broadcast': {
        backgroundColor: `${text.primary} !important`,
        color: `${white} !important`,
      },
      '&$typing': {
        backgroundColor: 'initial !important',
        padding: '0px !important',
      },
    },
    outbound: {},
    primary: {},
    pullBottom: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 5,
    },
    wrapper: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      '&$outbound': {
        justifyContent: 'flex-end !important',
      },
      '&$chatAudit': {
        color: colorDarkGray,
        padding: unit * 2,
        fontSize: 14,
        fontWeight: 500,
        display: 'inline-block',
        textAlign: 'center',
      },
    },
    '@keyframes blink': {
      '50%': {
        transform: 'scale(1.5)',
      },
    },
  });

export interface ChatMessageTypingProps extends WithStyles<typeof styles>, React.HTMLAttributes<HTMLDivElement> {
  fragmentRef: ChatMessageTyping_EmbedNode$key;
  delay?: number;
}

const ChatMessageTyping: React.FC<ChatMessageTypingProps> = ({ classes, fragmentRef, delay = 3000, ...rest }) => {
  const node = useFragment<ChatMessageTyping_EmbedNode$key>(
    graphql`
      fragment ChatMessageTyping_EmbedNode on Node {
        ... on Patient {
          _id
        }
        ... on Provider {
          _id
        }
        ... on User {
          _id
        }
        ...AvatarNode_EmbedNode
      }
    `,
    fragmentRef,
  );

  const timerRef = useRef(0);
  const [isTyping, setTyping] = useState(false);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useSocketEventsDebounced(['chat:server:typing:start'], (values) => {
    if (find(values, { id: node._id })) {
      setTyping(true);
      clearTimeout(timerRef.current);

      // Hide indicator after $delay not typing
      timerRef.current = window.setTimeout(() => {
        setTyping(false);
      }, delay);
    }
  });

  useSocketEventsDebounced(
    ['chat:client:inbound:message'],
    (values) => {
      // Hide indicator right after receiving a message
      if (find(values, { patient: node._id })) {
        setTyping(false);
      }
    },
    100,
  );

  if (!isTyping) {
    return null;
  }
  return (
    <div className={classes.root} {...rest}>
      <div className={classnames(classes.wrapper)}>
        <div className={classes.pullBottom}>
          <AvatarNode fragmentRef={node} size="xsmall" includeIsOnline={false} />
        </div>
        <div className={classnames(classes.messageWrapper, classes.typing)}>
          <div className={classes.typingIndicator}>
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withStyles(styles)(ChatMessageTyping);
